import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../config/axiosConfig.js";

function useFilterData({
	endpoint,
	pageSize = 10,
	queryParams = {},
	searchDebounceMs = 500,
	enabled = true,
	isPaginated = true,
}) {
	const [data, setData] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");

	const abortControllerRef = useRef(null);
	const searchTimeoutRef = useRef(null);

	const fetchData = useCallback(
		async (pageNumber, search, reset = false) => {
			if (isLoading || !enabled) return;

			// Cancel previous API request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create new request controller for the API request
			const controller = new AbortController();
			abortControllerRef.current = controller;

			setIsLoading(true);
			setError(null);

			try {
				const cleanParams = Object.entries(queryParams).reduce((acc, [key, value]) => {
					if (value !== null && value !== undefined && value !== "") {
						acc[key] = value;
					}
					return acc;
				}, {});

				const params = isPaginated
					? {
							page: pageNumber,
							per_page: pageSize,
							paginate: 1,
							search: search || undefined,
							...cleanParams,
						}
					: {
							search: search || undefined,
							...cleanParams,
						};

				const response = await axiosInstance.get(endpoint, {
					params,
					signal: controller.signal,
				});
				const result = response.data;

				let newData, currentPage, lastPage;

				if (isPaginated) {
					newData = result?.data || [];
					currentPage = result?.["current_page"] || pageNumber;
					lastPage = result?.["last_page"] || 1;
				} else {
					newData = Array.isArray(result) ? result : [];
					currentPage = 1;
					lastPage = 1;
				}

				setData((prev) => (reset ? newData : [...prev, ...newData]));
				setHasMore(isPaginated ? currentPage < lastPage : false);
				setPage(currentPage);
			} catch (err) {
				if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
					setError(err);
					console.error("Error fetching data:", err);
				}
			} finally {
				if (abortControllerRef.current === controller) {
					setIsLoading(false);
				}
			}
		},
		[endpoint, pageSize, JSON.stringify(queryParams), enabled, isLoading, isPaginated],
	);

	const loadMore = useCallback(() => {
		if (hasMore && !isLoading && enabled) {
			void fetchData(page + 1, false);
		}
	}, [page, hasMore, isLoading, enabled, fetchData]);

	const handleSearchChange = useCallback(
		(value) => {
			setSearchTerm(value);

			// Clear existing timeout
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}

			// If search is cleared fetch immediately
			if (value === "") {
				setData([]);
				setPage(1);
				setHasMore(true);

				if (enabled) {
					void fetchData(1, "", true);
				}
				return;
			}

			// Otherwise Debounce search
			searchTimeoutRef.current = setTimeout(() => {
				setData([]);
				setPage(1);
				setHasMore(true);
				if (enabled) {
					void fetchData(1, value, true);
				}
			}, searchDebounceMs);
		},
		[fetchData, searchDebounceMs, enabled],
	);

	const reset = useCallback(() => {
		setData([]);
		setPage(1);
		setHasMore(true);
		setSearchTerm("");
		if (enabled) {
			void fetchData(1, "", true);
		}
	}, [fetchData, enabled]);

	// Initial load
	useEffect(() => {
		if (enabled) {
			void fetchData(1, searchTerm, true);
		}

		return () => {
			if (abortControllerRef.current) abortControllerRef.current.abort();
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		};
	}, [endpoint, enabled]);

	// Reset and refetch when queryParams change
	useEffect(() => {
		setData([]);
		setPage(1);
		setHasMore(true);
		setSearchTerm("");
		if (enabled) {
			void fetchData(1, "", true);
		}
	}, [JSON.stringify(queryParams)]);

	return {
		data,
		isLoading,
		error,
		hasMore,
		loadMore,
		searchTerm,
		setSearchTerm: handleSearchChange,
		reset,
	};
}

export default useFilterData;
